import { Box, Tab, Tabs, Typography } from '@mui/material';
import ScheduleBox from './components/ScheduleBox';
import useNswagClient from '../../hooks/api/useNswagClient';
import { useContext, useEffect, useMemo, useState } from 'react';
import { CountZonesResponse, StockCheckScheduleDetail } from '../../app/services/api/generated';
import { UserContext } from '../../components/shared/useUser';
import { useTranslation } from 'react-i18next';
import PageTitle from '../../components/shared/PageTitle';
import PageArea from '../../components/shared/PageArea';
import CountingUOMBox from './components/CountingUomsBox';
import { ConfigurationName } from '../../enums/ConfigurationName';
import useLogError from '../../hooks/useLogError';
import CountZonesBox from './components/CountZonesBox';
import { useNavigate, useParams } from 'react-router-dom';
import { useConfigurations } from '../../components/shared/useConfigurations';

const tabIndexToValue: Record<string, string> = {
  '1': 'one',
  '2': 'two',
  '3': 'three',
};
const tabValueToIndex: Record<string, string> = {
  'one': '1',
  'two': '2',
  'three': '3',
};

const StockCountSchedule = () => {
  const { getsStockCheckSchedule, getCountZones } = useNswagClient();
  const { isConfigurationEnabled } = useConfigurations();
  const { t } = useTranslation('common');
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const storeID = selectedStore?.storeNumber ? selectedStore.storeNumber : 'No store selected';
  const isStoreSelected = () => { if (selectedStore == null) { return false; } else { return true; } };
  const [schedules, setSchedules] = useState<StockCheckScheduleDetail[]>([]);
  const [tabValue, setTabValue] = useState<string>('one');
  const isCountingInDifferentUom = isConfigurationEnabled(ConfigurationName.CountingInDifferentUoms);
  const isCountZonesEnabled = isConfigurationEnabled(ConfigurationName.CountZonesWithItemsEnabled);
  const [zones, setZones] = useState<CountZonesResponse[]>([]);
  const [isSchedulesLoading, setIsSchedulesLoading] = useState(false);
  const [isZonesLoading, setIsZonesLoading] = useState(false);
  const [zonesLoaded, setZonesLoaded] = useState(false);
  const [showAllStores, setShowAllStores] = useState(false);
  const { logError } = useLogError();
  const navigate = useNavigate();
  const params = useParams<{ tab?: string }>();

  const canSeeAllTabs = hasPermissionTo(['StockCountAdmin']);
  const canSeeScheduleTab = canSeeAllTabs || hasPermissionTo(['StockCountScheduleRead']);
  const canSeeUomTab = (canSeeAllTabs || hasPermissionTo(['StockCountUomsRead'])) && isCountingInDifferentUom;
  const canSeeZonesTab = (canSeeAllTabs || hasPermissionTo(['StockLocationRead'])) && isCountZonesEnabled;

  const availableTabs = useMemo(() => {
    const tabs: string[] = [];

    if (canSeeScheduleTab) {
      tabs.push('one');
    }

    if (canSeeUomTab) {
      tabs.push('two');
    }

    if (canSeeZonesTab) {
      tabs.push('three');
    }

    return tabs;
  }, [canSeeScheduleTab, canSeeUomTab, canSeeZonesTab]);

  useEffect(() => {
    loadData();
  }, [selectedStore]);

  useEffect(() => {
    let nextTab = availableTabs[0] ?? 'one';

    if (params.tab && tabIndexToValue[params.tab]) {
      const candidate = tabIndexToValue[params.tab];
      if (availableTabs.includes(candidate)) {
        nextTab = candidate;
      }
    }

    setTabValue(nextTab);
  }, [params.tab, availableTabs]);

  useEffect(() => {
    if (tabValue === 'three' && canSeeZonesTab && !zonesLoaded) {
      loadZones();
    }
  }, [tabValue, canSeeZonesTab]);

  const loadData = (allStores?: boolean) => {
    setIsSchedulesLoading(true);
    getsStockCheckSchedule(storeID, allStores)
      .then((result) => {
        if (result?.data) setSchedules(result.data);
      })
      .catch(logError)
      .finally(() => setIsSchedulesLoading(false));
  };

  const loadZones = (itemNumber?: string) => {
    setIsZonesLoading(true);
    getCountZones(storeID, itemNumber)
      .then((result) => {
        if (result?.data) setZones(result.data);
        setZonesLoaded(true);
      })
      .catch(logError)
      .finally(() => setIsZonesLoading(false));
  };

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
    navigate(`/stock-count-schedule/${tabValueToIndex[newValue]}`);

    if (newValue === 'three' && canSeeZonesTab) {
      loadZones();
    }
  };


  return (
    <Box sx={{ width: '100%', textAlign: 'left' }}>
      <PageArea>
        <PageTitle>{t('stockCountManagement')}</PageTitle>
        {isStoreSelected() ? <>
          <Tabs value={tabValue}
            onChange={handleChange}
            textColor="primary"
            indicatorColor="primary"
            aria-label="primary tabs example">
            {canSeeScheduleTab && <Tab label={t('scheduledStockCounts')}
              value="one" />}
            {canSeeUomTab && <Tab label={t('countingUOMs')}
              value="two" />}
            {canSeeZonesTab && <Tab label={t('countZones')}
              value="three" />}
          </Tabs>
          {tabValue === 'one' && canSeeScheduleTab && <ScheduleBox schedules={schedules}
            storeID={storeID}
            loadData={loadData}
            isLoading={isSchedulesLoading}
            showAllStores={showAllStores}
            setShowAllStores={setShowAllStores}></ScheduleBox>}
          {tabValue === 'two' && canSeeUomTab && <CountingUOMBox></CountingUOMBox>}
          {tabValue === 'three' && canSeeZonesTab && <CountZonesBox zones={zones}
            storeID={storeID}
            loadData={loadZones}
            isLoading={isZonesLoading}></CountZonesBox>}
          {availableTabs.length === 0 && <Box><Typography variant='textLG'>No tabs available for your current permissions.</Typography></Box>}
        </>
          : <Box><Typography variant='textLG'>No store selected</Typography></Box>}
      </PageArea>
    </Box>
  );
};

export default StockCountSchedule;